<py-drawer-template
  [header]="header"
  [body]="body"
  [footer]="footer"
  [children]="drawerChildren"
  [(activeTemplateKey)]="activeTemplateKey"
  [title]="'cart.page_title' | cxTranslate"
></py-drawer-template>

<ng-template #header>
  <div class="header" #headerSection>
    <div class="content">
      <ng-container *ngIf="isMaxOrderLinesExceeded$ | async as isMaxOrderLinesExceeded">
        <div *ngIf="isMaxOrderLinesExceeded" class="mb-5">
          <py-notification
            class="mb-2"
            type="info"
            [tiny]="true"
            [showBorder]="false"
            [message]="'cart.maxOrderLinesExceeded_text' | cxTranslate"
          >
          </py-notification>

          <cx-generic-link
            [routerUrl]="{ cxRoute: 'checkout' } | cxUrl"
            [queryParams]="{ cartType: cartType$ | async }"
            class="d-block text-center"
          >
            {{ 'cart.goToCheckout_action' | cxTranslate }}
            <i nz-icon nzType="arrow-right"></i
          ></cx-generic-link>
        </div>
      </ng-container>
      <py-notification
        *ngIf="hasDiscontinuedArticles$ | async"
        type="notification"
        [tiny]="true"
        [showBorder]="false"
        [message]="'cart.containsDiscontinuedArticles_message' | cxTranslate"
      >
      </py-notification>
      <py-notification
        *ngIf="prePrintedLabelEntryMixedWithNonPrePrintedEntry$ | async"
        type="warning"
        [tiny]="true"
        [showBorder]="false"
        [message]="'cart.containsPrePrintedLabelConfigurations_message' | cxTranslate"
      >
      </py-notification>
      <ul class="notifications">
        <li *ngFor="let notification of notifications$ | async">
          <py-notification
            [type]="'info'"
            [tiny]="true"
            [showBorder]="false"
            [customIcon]="notification.icon"
            [message]="notification.text"
          >
          </py-notification>
        </li>
      </ul>
      <py-notification
        *ngIf="isCreditBlocked$ | async"
        [type]="'danger'"
        [tiny]="true"
        [showBorder]="false"
        [message]="'cart.creditLimitExceeded_text' | cxTranslate"
      ></py-notification>
      <ng-container>
        <py-none-accounting-codes-notification
          *ngIf="accountingCodesMandatoryButNoneAvailable$ | async"
          [tiny]="true"
          [showBorder]="false"
        ></py-none-accounting-codes-notification>
      </ng-container>
      <div class="nav-container" *ngIf="hasMultipleTinyCarts$ | async">
        <ng-container *ngIf="tinyCarts$ | async as tinyCarts">
          <ng-container *ngIf="selectedTinyCartCode$ | async as selectedTinyCartCode">
            <ul
              #nav="ngbNav"
              ngbNav
              pyNavSideScroll
              [listCount]="tinyCarts?.length"
              class="nav-tabs active-tab-border side-scroll multiple-carts"
              [activeId]="selectedTinyCartCode"
              (activeIdChange)="onTinyCartSelect($event)"
            >
              <li
                class="d-flex align-items-center pr-3"
                [ngClass]="{ 'w-50': tinyCarts?.length < 3 }"
                *ngFor="let tinyCart of tinyCarts"
                [ngbNavItem]="tinyCart.code"
                role="presentation"
              >
                <a class="mr-0" ngbNavLink
                  >{{ 'cart.cartType' | cxTranslate: { context: tinyCart.cartType }
                  }}<py-badge [tag]="true" class="ml-2">{{ tinyCart.numberOfEntries }}</py-badge></a
                >
              </li>
            </ul>
            <div [ngbNavOutlet]="nav"></div>
          </ng-container>
        </ng-container>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #body>
  <div #bodySection>
    <ng-container *ngIf="showEmptyNotification$ | async; else cartInfo">
      <py-cart-empty-notification></py-cart-empty-notification>
    </ng-container>

    <ng-template #cartInfo>
      <div *ngIf="hasCartEditStatus$ | async" class="edit-notification py-3 px-2 mb-4">
        <span
          >{{ 'cart.editingOrder_hint' | cxTranslate }} <u>{{ approvalOrderRef$ | async }}</u></span
        >
        <span class="mx-3">|</span>
        <button type="button" class="link edit-notification__link" (click)="onAbortCartEdit()">
          <i nz-icon nzType="close-circle" nzTheme="outline"></i>
          <span class="ml-1">{{ 'cart.abortEdit_action' | cxTranslate }}</span>
        </button>
      </div>

      <div class="d-flex flex-row mt-3 cart-actions">
        <ng-container *pyPrincipalConfiguration="'enableOrderTemplates'">
          <button
            *ngIf="cartType === stockCartType"
            class="btn btn-ternary btn-icon mr-3 d-flex align-items-center justify-content-center"
            [ngbTooltip]="'cart.createShoppingList_tooltip' | cxTranslate"
            [attr.aria-label]="'cart.createShoppingList_action' | cxTranslate"
            [disableTooltip]="!(hasDiscontinuedArticles$ | async)"
            [container]="'body'"
            (click)="onCreateShoppingList()"
          >
            <i nz-icon nzType="heart"></i>
          </button>
        </ng-container>

        <ng-container *pyPrincipalConfiguration="'enableOrderCardsAdmin'">
          <py-add-to-order-card class="d-block mr-3" [entries]="entries$ | async"></py-add-to-order-card>
        </ng-container>

        <button class="btn btn-ternary text-danger d-flex align-items-center justify-content-center" (click)="onEmptyCart()">
          <i nz-icon nzType="delete"></i>
          <span class="ml-2">{{ 'cart.emptyCart_action' | cxTranslate }}</span>
        </button>
      </div>

      <hr class="mx-3" />

      <py-cart-entries-table
        #drawerCartEntriesTableChild
        id="drawer-cart-entries-table"
        [entries]="entries$ | async"
        [totalPrice]="totalPrice$ | async"
        [loading]="loading$ | async"
        [formGroup]="formGroup"
        [isCartWriter]="true"
        [accountingCodeList$]="accountingCodeList$"
        [accountingCodeListLoading]="accountingCodeListLoading$ | async"
        [numberOfLoadingEntries]="numberOfEntries$ | async"
        [cartType]="cartType$ | async"
        [cartErrors]="cartErrors$ | async"
        (removeEntry)="onCartEntryRemove($event)"
        (replaceEntry)="onCartEntryReplace($event)"
        (updateEntry)="onCartEntryUpdateQuantity($event)"
        (updateEntryNote)="onCartEntryUpdateNote($event)"
        (updateEntryStatisticsCode)="onCartEntryUpdateStatisticsCode($event)"
      >
      </py-cart-entries-table>
    </ng-template>
  </div>
</ng-template>

<ng-template #footer>
  <div class="footer">
    <div class="d-flex flex-row justify-content-between">
      <ng-container *ngIf="{ numberOfEntries: numberOfEntries$ | async } as data">
        <p *ngIf="data.numberOfEntries != null" class="w-25 items">
          {{ 'cart.numberOfItems_hint' | cxTranslate: { count: data.numberOfEntries } }}
        </p>
      </ng-container>
      <p class="order-value" *pyPrincipalConfiguration="'enableDisplayOfPrices'">
        <span class="h6">
          {{ textInsteadOfPrice ? (textInsteadOfPrice | cxTranslate) : (totalPrice$ | async | price: 'symbol-narrow' : true) }}
          {{ 'cart.total_heading' | lowercase | cxTranslate }}
        </span>
      </p>
    </div>

    <div class="d-flex flex-column flex-md-row flex-wrap mt-3 button-group">
      <button
        class="btn btn-secondary flex-fill"
        id="view-cart"
        [disabled]="!(isUserLoggedIn$ | async)"
        (click)="onGoToCart(cartTypeQueryParams || { cartType: stockCartType })"
      >
        {{ 'cart.viewCart_action' | cxTranslate }}
      </button>
      <button
        class="btn btn-primary flex-fill"
        [disabled]="
          !(isCheckoutEnabled$ | async) ||
          (formGroup.invalid && formGroup.touched) ||
          (accountingCodesMandatoryButNoneAvailable$ | async) ||
          (loading$ | async)
        "
        (click)="onGoToCheckout()"
      >
        {{ 'cart.goToCheckout_action' | cxTranslate }}
      </button>
    </div>
  </div>
</ng-template>
